import { msalInstance, loginRequest } from '../config/auth';
import type { JournalSummary, JournalView, Continuation, ContinuationEvent, GoalFrame, BudgetVector, Finding } from '../types';
import type { Roadmap } from '../roadmapTypes';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

/** What `journalApi.stream` hands back. Only `close()` is used by callers. */
export interface StreamHandle { close: () => void }

function parseJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return null; }
}

async function getToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  const account = msalInstance.getActiveAccount() || accounts[0];
  try {
    const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return response.accessToken;
  } catch {
    try {
      const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
      return response.accessToken;
    } catch {
      return null;
    }
  }
}

export async function copilotHeaders(): Promise<Record<string, string>> { return headers(); }
/** The signed-in user's access token, for same-origin /viz pages that call the API from inside VizPage's iframe. */
export async function getAccessToken(): Promise<string | null> { return getToken(); }

async function headers(): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await getToken();
  if (token) h['Authorization'] = `Bearer ${token}`;

  let userId: string | undefined;

  let account = msalInstance.getActiveAccount();
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    }
  }

  if (account?.localAccountId) {
    userId = account.localAccountId;
  } else if (account?.homeAccountId) {
    userId = account.homeAccountId.split('.')[0];
  }

  if (!userId && token) {
    const claims = parseJwtClaims(token);
    if (claims) {
      userId = (claims.oid || claims.sub || claims.upn) as string | undefined;
    }
  }

  if (!userId) {
    userId = 'maestro-anonymous';
  }

  h['x-user-id'] = userId;
  h['x-tenant-id'] = userId;

  return h;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { headers: await headers() });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers: await headers(), body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

export const journalApi = {
  list: (limit = 20) => get<JournalSummary[]>(`/journals?limit=${limit}`),
  get: (rootId: string) => get<JournalView>(`/journals/${rootId}`),
  findings: (rootId: string) => get<(Finding & { continuationId: string; continuationIntent: string })[]>(`/journals/${rootId}/findings`),
  budget: (rootId: string) => get<{ totalBudget: BudgetVector; spentBudget: { tokens: number; dollars: number; activeSeconds: number }; perContinuation: unknown[] }>(`/journals/${rootId}/budget`),
  /**
   * Live journal events.
   *
   * This used `EventSource`, which cannot set an Authorization header — the route was
   * reachable because it authorised on `x-tenant-id`, and EventSource cannot send that
   * either, so the stream was simply open. Now that the route requires a verified token,
   * the stream is read with `fetch` and the SSE frames are parsed here. The alternative,
   * a token in the query string, writes the credential into every access log and proxy
   * cache along the path.
   */
  stream: (rootId: string, onEvent: (evt: ContinuationEvent) => void, onError?: (err: unknown) => void): StreamHandle => {
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/journals/${rootId}/stream`, {
          headers: await headers(),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) {
          throw new Error(res.status === 401
            ? 'Session expired — sign in again to follow this journal live'
            : `Stream failed: ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line; a frame may span reads, so only
          // whole frames are consumed and the remainder stays in the buffer.
          let sep: number;
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            for (const line of frame.split('\n')) {
              if (!line.startsWith('data:')) continue;
              try { onEvent(JSON.parse(line.slice(5).trim())); } catch { /* skip malformed */ }
            }
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;   // stopStream(), not a failure
        onError?.(err);
      }
    })();

    return { close: () => controller.abort() };
  },
};

export const continuationApi = {
  create: (body: { goalFrame: GoalFrame; budget?: BudgetVector; wakeConditions?: unknown[]; tags?: string[] }) =>
    post<Continuation>('/continuations', body),
  get: (id: string) => get<Continuation & { recentEvents: ContinuationEvent[] }>(`/continuations/${id}`),
  events: (id: string, limit = 50, afterSequence?: number) =>
    get<ContinuationEvent[]>(`/continuations/${id}/events?limit=${limit}${afterSequence !== undefined ? `&afterSequence=${afterSequence}` : ''}`),
  field: (id: string) => get<unknown>(`/continuations/${id}/field`),
  signal: (id: string, type: 'approve' | 'redirect' | 'kill', message?: string, goalFrame?: Partial<GoalFrame>) =>
    post<{ signaled: boolean }>(`/continuations/${id}/signal`, { type, message, goalFrame }),
  fork: (id: string, goalFrame: GoalFrame, budget?: BudgetVector) =>
    post<Continuation>(`/continuations/${id}/fork`, { goalFrame, budget }),
  tick: (id: string) => post<{ success: boolean; event?: ContinuationEvent; error?: string }>(`/continuations/${id}/tick`, {}),
  annotate: (id: string, content: string, tags?: string[]) =>
    post<{ annotated: boolean }>(`/continuations/${id}/annotate`, { content, tags }),
};

export interface VersionedArtifact {
  id: string;
  artifactId: string;
  tenantId: string;
  plane: 'sql' | 'prompt' | 'mcp-tool';
  name: string;
  version: number;
  isLive: boolean;
  isDraft: boolean;
  content: SqlContent | PromptContent | McpToolContent;
  dataSource?: string;
  tags: string[];
  createdBy: string;
  createdAt: string;
  changeNote: string;
  parentVersionId?: string;
  validation: { status: string; lastTestedAt?: string; rowCount?: number; executionMs?: number; testResultSummary?: string };
}

export interface SqlContent {
  type: 'sql';
  sqlTemplate: string;
  paramStyle: 'interpolated' | 'positional';
  params: Array<{ name: string; type: string; required: boolean; description: string }>;
  outputColumns: Array<{ name: string; description: string }>;
  defaultLimit: number;
  category: string;
  tables: string[];
  guardConfig: { maxRows: number; timeoutMs: number; maxBytesResponse: number; rejectDml: boolean };
}

export interface PromptContent { type: 'prompt'; systemPrompt: string; toolBindings: string[]; model: string; temperature: number; maxTokens: number; variables: Array<{ name: string; description: string; defaultValue?: string; required: boolean }>; agentId?: string; }
export interface McpToolContent { type: 'mcp-tool'; toolName: string; description: string; parameters: unknown; backend: string; sqlArtifactId?: string; dataSourceKey?: string; }

export interface ArtifactEvent {
  id: string;
  artifactId: string;
  versionId: string;
  ts: string;
  kind: string;
  actor: string;
  payload: Record<string, unknown>;
  testResult?: { success: boolean; rowCount: number; executionMs: number; errorMessage?: string; sampleRows?: unknown[] };
}

export interface TestResult {
  success: boolean;
  rowCount: number;
  executionMs: number;
  rows?: unknown[];
  sampleRows?: unknown[];
  sql: string;
  error?: string;
}

export const artifactApi = {
  list: (plane?: string, dataSource?: string) => {
    const params = new URLSearchParams();
    if (plane) params.set('plane', plane);
    if (dataSource) params.set('dataSource', dataSource);
    const qs = params.toString();
    return get<VersionedArtifact[]>(`/studio/artifacts${qs ? `?${qs}` : ''}`);
  },
  get: (artifactId: string) => get<VersionedArtifact>(`/studio/artifacts/${artifactId}`),
  versions: (artifactId: string) => get<VersionedArtifact[]>(`/studio/artifacts/${artifactId}/versions`),
  events: (artifactId: string) => get<ArtifactEvent[]>(`/studio/artifacts/${artifactId}/events`),
  create: (body: { name: string; plane: string; content: unknown; dataSource?: string; tags?: string[]; changeNote?: string }) =>
    post<VersionedArtifact>('/studio/artifacts', body),
  createVersion: (artifactId: string, body: { content: unknown; changeNote: string }) =>
    post<VersionedArtifact>(`/studio/artifacts/${artifactId}/versions`, body),
  promote: (artifactId: string, versionId: string) =>
    post<{ promoted: boolean; version: number }>(`/studio/artifacts/${artifactId}/promote/${versionId}`, {}),
  rollback: (artifactId: string) =>
    post<{ rolledBack: boolean }>(`/studio/artifacts/${artifactId}/rollback`, {}),
  test: (artifactId: string, body: { sql?: string; params?: Record<string, unknown>; dataSourceKey?: string; dryRun?: boolean }) =>
    post<TestResult>(`/studio/artifacts/${artifactId}/test`, body),
};

export interface DataSourceInfo {
  id: string;
  name: string;
  type: 'postgresql' | 'redshift' | 'cosmos';
  product: string;
  env: string;
  host?: string;
  database?: string;
  isDefault?: boolean;
  isEnabled?: boolean;
  authorityRequired?: number;
}

export const dataSourceApi = {
  list: () => get<DataSourceInfo[]>('/data-sources'),
  testConnection: (id: string) =>
    post<{ ok: boolean; latencyMs?: number; error?: string }>(`/data-sources/${id}/test-connection`, {}),
};

// ── ActiveContext (KB Doc 142) ──

export interface ScopedRef {
  name: string; id: string | null; key: string | null;
  resolvedFrom: string | null; resolvedAt: string | null;
}
export interface ActiveContext {
  schemaProfile: string; journalId: string; revision: number;
  customer: ScopedRef; site: ScopedRef; diet: ScopedRef;
  class: string | null; subclass: string | null;
  primarySource: string; snapshotAt: string | null;
  basis: 'as-fed' | 'DM' | 'unspecified';
  flags: { stale: boolean; ambiguous: boolean; conflict: boolean; unresolved: boolean };
  notes: string[]; setBy: string; updatedAt: string; freshnessTtlMinutes: number;
}
export interface ContextPatch {
  customer?: Partial<ScopedRef>; site?: Partial<ScopedRef>; diet?: Partial<ScopedRef>;
  class?: string | null; subclass?: string | null; basis?: 'as-fed' | 'DM' | 'unspecified';
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { method: 'PUT', headers: await headers(), body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

/**
 * Decision sets (KB 172). Reads are open — the gallery calls `/decision-sets` with a bare fetch so
 * it works before sign-in resolves — but publishing goes through here, because `put` attaches the
 * MSAL bearer and the endpoint requires one.
 */
export interface DecisionSetSummary {
  id: string; title: string; subtitle?: string; status: string;
  visibility: 'public' | 'tenant';
  rows: number; options: number; decided: number;
  version: number; updatedAt: string; updatedBy: string; href: string;
}

export interface PublishResult extends DecisionSetSummary {
  supersededVersion: number | null;
}

export const decisionSetApi = {
  list: () => get<{ sets: DecisionSetSummary[] }>('/decision-sets'),
  get: (id: string, version?: number) =>
    get<{ decisionSet: Record<string, unknown>; meta: Record<string, unknown> }>(
      `/decision-sets/${encodeURIComponent(id)}${version ? `?version=${version}` : ''}`,
    ),
  versions: (id: string) =>
    get<{ versions: DecisionSetSummary[] }>(`/decision-sets/${encodeURIComponent(id)}/versions`),
  publish: (document: { id: string }, opts: { visibility?: 'public' | 'tenant'; changeNote?: string } = {}) =>
    put<PublishResult>(`/decision-sets/${encodeURIComponent(document.id)}`, { document, ...opts }),
};

/**
 * The five-lane alignment feed (ROADMAP_APP.md Phase 2).
 *
 * It goes through `get()` — i.e. through `headers()` — for one reason that is the whole
 * point of the route: the owner's 2026-09-16 ruling is that the Roadmap is NOT public.
 * `decisionSetApi.list` above reaches an endpoint that deliberately answers anonymous
 * callers; this one reaches an endpoint that 401s them. Fetching it with a bare `fetch`
 * (as `VizPage` does for the public gallery) would therefore not "work without a token" —
 * it would fail, which is correct, but it would fail in a way that looks like an outage.
 */
export const roadmapApi = {
  get: () => get<Roadmap>('/roadmap'),
};

export const contextApi = {
  get: (journalId: string) => get<ActiveContext>(`/journals/${journalId}/context`),
  set: (journalId: string, patch: ContextPatch, reason?: string) =>
    put<ActiveContext>(`/journals/${journalId}/context`, { patch, reason }),
  resolve: (journalId: string) =>
    post<ActiveContext>(`/journals/${journalId}/context/resolve`, {}),
  events: (journalId: string) =>
    get<Array<{ id: string; ts: string; actor: string; kind: string; reason?: string }>>(`/journals/${journalId}/context/events`),
};
